import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

import { Button } from '../interactive/button.jsx';
import { InputField } from '../interactive/input-field.jsx';

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string | import('react').ReactNode} props.message
 * @param {() => void} [props.onConfirm]
 * @param {() => void} [props.onCancel]
 * @param {string} [props.confirmLabel]
 * @param {string} [props.cancelLabel]
 * @param {boolean} [props.loading]
 * @param {string} [props.requireTyping] - when set, user must type this text to enable confirm
 * @param {string} [props.typingPlaceholder] - placeholder for the type-to-confirm input
 * @returns {JSX.Element}
 */
export function Dialog({ title, message, onConfirm, onCancel, confirmLabel, cancelLabel, loading, requireTyping, typingPlaceholder }) {
	const { t } = useTranslation();
	const [typedValue, setTypedValue] = useState('');
	const canConfirm = !requireTyping || typedValue === requireTyping;

	useEffect(() => {
		setTypedValue('');
	}, [requireTyping]);

	useEffect(() => {
		/**
		 * @param {KeyboardEvent} e
		 */
		function handleKey(e) {
			// Ignore key auto-repeat so a held Enter can't fire confirm by itself.
			if (e.repeat) { return; }
			if (e.key === 'Escape' && onCancel) {
				onCancel();
			}
			if (e.key === 'Enter' && onConfirm && canConfirm) {
				onConfirm();
			}
		}
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [onConfirm, onCancel, canConfirm]);

	return (
		<div className="modal-backdrop" onClick={onCancel}>
			<div className="modal" onClick={(e) => e.stopPropagation()}>
				<div className="modal-title">{title}</div>
				<div className="modal-message">
					{typeof message === 'string'
						? message.split('\n').map((line, i) => (
							<div key={i} className="modal-message-line">{line || '\u00A0'}</div>
						))
						: message}
				</div>
				{requireTyping && (
					<div className="modal-input-row">
						<InputField
							value={typedValue}
							onChange={(v) => setTypedValue(v)}
							placeholder={typingPlaceholder || t('dialog.typeToConfirm')}
							autoFocus
							disabled={loading}
						/>
					</div>
				)}
				<div className="modal-actions">
					{onCancel && (
						<Button onClick={onCancel} disabled={loading}>
							{cancelLabel || t('btn.cancel')}
						</Button>
					)}
					{onConfirm && (
						<Button variant="primary" autoFocus={!requireTyping} onClick={onConfirm} loading={loading} disabled={!canConfirm}>
							{loading ? t('btn.working') : (confirmLabel || t('btn.ok'))}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
